import { Fragment } from 'react';
import { VisuallyHidden } from '@reach/visually-hidden';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import {
  faAdd, faAngleDown, faAngleUp, faCaretDown, faCaretUp, faCheckSquare, faEdit, faEllipsisH, faEllipsisV,
  faSort, faTrashAlt, faRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import useSelectIcon from './UseSelectIcon';
import useExpandIcon from './UseExpandIcon';
import useSelectAllIcon from './UseSelectAllIcon';
import useExpandAllIcon from './UseExpandAllIcon';
import { SORT_DIRECTIONS } from '../../utils/TableConstants';

import tableStyle from '../../Table.module.css';

const Icon = ({ toolTipText, iconObj }) => (
  <Fragment>
    <VisuallyHidden>{toolTipText}</VisuallyHidden>
    <FontAwesomeIcon title={toolTipText} icon={iconObj} style={{ padding: '0.2em', borderRadius: '25%' }} fixedWidth />
  </Fragment>
);

/**
 * Styling component for the delete icon.
 * @param {Function} onClick function to be called when the icon is clicked 
 */
const Undo = ({ onClick, toolTipText = 'Undo', className = '', style = undefined }) => (
  <button type='button' onClick={onClick} className={[tableStyle.Icon, tableStyle.Undo, className].join(' ')} style={style}>
    <Icon toolTipText={toolTipText} iconObj={faRotateLeft} />
  </button>
);

/**
 * Styling component for the delete icon.
 * @param {Function} onClick function to be called when the icon is clicked 
 */
const Delete = ({ onClick, toolTipText = 'Delete', className = '', style = undefined }) => (
  <button type='button' onClick={onClick} className={[tableStyle.Icon, tableStyle.Delete, className].join(' ')} style={style}>
    <Icon toolTipText={toolTipText} iconObj={faTrashAlt} />
  </button>
);

/**
 * Styling component for the edit icon.
 * @param {Function} onClick function to be called when the icon is clicked 
 */
const Edit = ({ onClick, toolTipText = 'Edit', className = '', style = undefined }) => (
  <button type='button' onClick={onClick} className={[tableStyle.Icon, tableStyle.Edit, className].join(' ')} style={style}>
    <Icon toolTipText={toolTipText} iconObj={faEdit} />
  </button>
);

/**
 * Styling component for the add icon.
 * @param {Function} onClick function to be called when the icon is clicked 
 */
const Add = ({ onClick, toolTipText = 'Add', className = '', style = undefined }) => (
  <button type='button' onClick={onClick} className={[tableStyle.Icon, tableStyle.Add, className].join(' ')} style={style}>
    <Icon toolTipText={toolTipText} iconObj={faAdd} />
  </button>
);

/**
 * Styling component for the sort icon.
 * Displays different icons depending on the sort direction.
 * @param {Function} onClick function to be called when the icon is clicked 
 */
const Sort = ({ onClick, toolTipText = 'Sort', isSorted = false, sortDirection = '', className = '', style = undefined }) => (
  <button type='button' onClick={onClick} className={[tableStyle.Icon, tableStyle.Sort, className].join(' ')} style={style}>
    {isSorted === true && sortDirection === SORT_DIRECTIONS.ASCENDING
      ? <Icon toolTipText={toolTipText} iconObj={faCaretUp} />
      : isSorted === true && sortDirection === SORT_DIRECTIONS.DESCENDING
        ? <Icon toolTipText={toolTipText} iconObj={faCaretDown} />
        : <Icon toolTipText={toolTipText} iconObj={faSort} />
    }
  </button>
);

/**
 * Functional component for the select/unselect all icon.
 * Will select/unselect all rows in the table when clicked.
 */
const SelectAll = ({ excludedIds = [], className = '', style = undefined }) => {
  const { tableHasData, areAllSelected, onIconClicked } = useSelectAllIcon(excludedIds);

  if (tableHasData === true) {
    return (
      <button type='button' onClick={onIconClicked} className={[tableStyle.Icon, tableStyle.SelectAll, className].join(' ')} style={style}>
        {areAllSelected === true
          ? <Icon toolTipText={'Unselect All'} iconObj={faCheckSquare} />
          : <Icon toolTipText={'Select All'} iconObj={faSquare} />
        }
      </button>
    );
  } else {
    return <Fragment />;
  }
};

/**
 * Functional component for the select/unselect icon.
 * Will select/unselect a single row when clicked.
 * @param {Number} rowId unique id of the row, i.e. person.personId 
 */
const Select = ({ rowId, className = '', style = undefined }) => {
  const { isSelected, onIconClicked } = useSelectIcon(rowId);

  return (
    <button type='button' onClick={onIconClicked} className={[tableStyle.Icon, tableStyle.Select, className].join(' ')} style={style}>
      {isSelected === true
        ? <Icon toolTipText={'Unselect'} iconObj={faCheckSquare} />
        : <Icon toolTipText={'Select'} iconObj={faSquare} />
      }
    </button>
  );
};

/**
 * Functional component for the expand/collapse all icon.
 * Will expand/collapse all rows in the table when clicked.
 */
const ExpandAll = ({ hideText = 'Hide All', showText = 'Show All', className = '', style = undefined }) => {
  const { tableHasData, areAllExpanded, onIconClicked } = useExpandAllIcon();

  if (tableHasData === true) {
    return (
      <button type='button' onClick={onIconClicked} className={[tableStyle.Icon, tableStyle.ExpandAll, className].join(' ')} style={style}>
        {areAllExpanded === true
          ? <Icon toolTipText={hideText} iconObj={faAngleUp} />
          : <Icon toolTipText={showText} iconObj={faAngleDown} />
        }
      </button>
    );
  } else {
    return <Fragment />;
  }
};

/**
 * Functional component for the ellipsis icon.
 * This specific ellipsis is meant to be used in an accordion row that contains action buttons.
 * @param {Number} rowId unique id of the row, i.e. person.personId 
 */
const EllipsisExpand = ({ rowId, hideText = 'Hide', showText = 'Show', className = '', style = undefined }) => {
  const { isExpanded, onIconClicked } = useExpandIcon(rowId);

  return (
    <button type='button' onClick={onIconClicked} className={[tableStyle.Icon, tableStyle.Ellipsis, className].join(' ')} style={style}>
      {isExpanded === true
        ? <Icon toolTipText={hideText} iconObj={faEllipsisH} />
        : <Icon toolTipText={showText} iconObj={faEllipsisV} />
      }
    </button>
  );
};

/**
 * Functional component for the expand/collapse icon.
 * Will expand/collapse a single row when clicked.
 * @param {Number} rowId unique id of the row, i.e. person.personId 
 */
const Expand = ({ rowId, hideText = 'Hide', showText = 'Show', className = '', style = undefined }) => {
  const { isExpanded, onIconClicked } = useExpandIcon(rowId);

  return (
    <button type='button' onClick={onIconClicked} className={[tableStyle.Icon, tableStyle.Expand, className].join(' ')} style={style}>
      {isExpanded === true
        ? <Icon toolTipText={hideText} iconObj={faAngleUp} />
        : <Icon toolTipText={showText} iconObj={faAngleDown} />
      }
    </button>
  );
};

export { Expand, ExpandAll, EllipsisExpand, Select, SelectAll, Add, Edit, Delete, Sort, Undo };